import { useCallback, useRef, useState } from 'react';
import { type OperationVariables, type MutationResult } from '@apollo/react-common';
import {
	type MutationHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, commitMutation, useRelayEnvironment } from 'react-relay';
import { type DocumentNode } from 'graphql/language/ast';

import { useMemoObj } from './useMemoObj';

// For now, only a limited number of parameters are supported in a Relay variant.
// This type serves as an indicator of what's supported.
// To find all the unsupported usages, `UseMutationInteropCompatibleHookValues` tuple  may be replaced with it - then Typecheck will highlight everything that's broken
type SupportedUseMutationInteropCompatibleHookValues<TData, TVariables> = [
	(options?: { variables?: TVariables }) => Promise<{ data?: TData }>,
	Pick<MutationResult<TData>, 'data' | 'error' | 'loading'>,
];
export type UseMutationInteropCompatibleHookValues<TData, TVariables> = [
	(options?: { variables?: TVariables }) => Promise<{ data?: TData }>,
	MutationResult<TData>,
];

export type UseMutationInteropParams<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [DocumentNode | GraphQLTaggedNode]
	| [DocumentNode | GraphQLTaggedNode, MutationHookOptions<TData, TVariables>];

export function useMutationInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...[mutation, options]: UseMutationInteropParams<TData, TVariables>
): UseMutationInteropCompatibleHookValues<TData, TVariables> {
	return process.env.BUILD_VARIANT === 'relay'
		? (__test_only_useMutationInterop_Relay(
				...[mutation as GraphQLTaggedNode, options],
			) as UseMutationInteropCompatibleHookValues<TData, TVariables>)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useMutation<TData, TVariables>(...[mutation as DocumentNode, options]);
}

function __test_only_useMutationInterop_Relay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	mutation: GraphQLTaggedNode,
	options?: Parameters<typeof useMutation<TData, TVariables>>[1],
): SupportedUseMutationInteropCompatibleHookValues<TData, TVariables> {
	const environment = useRelayEnvironment();
	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables);

	// State to manage the mutation result
	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<any>(undefined);

	const onCompletedRef = useRef(options?.onCompleted);
	onCompletedRef.current = options?.onCompleted;

	// Create the mutation function
	const mutate = useCallback(
		(mutationOptions?: { variables?: TVariables }) => {
			setLoading(true);
			setError(undefined);

			const operationVariables = mutationOptions?.variables || variables || {};

			return new Promise<{ data?: TData }>((resolve, reject) => {
				commitMutation(environment, {
					mutation,
					variables: operationVariables,
					onCompleted: (response: any, errors) => {
						if (errors && errors.length > 0) {
							const error = new Error(errors[0].message);
							setError(error);
							reject(error);
						} else {
							setData(response as TData);
							// Call the user-provided onCompleted callback only on success (to match Apollo behavior)
							onCompletedRef.current?.(response);
							resolve({ data: response as TData });
						}
						setLoading(false);
					},
					onError: (error: any) => {
						setLoading(false);
						setError(error);
						reject(error);
					},
				});
			});
		},
		[environment, mutation, variables],
	);

	return [
		mutate,
		{
			data,
			loading,
			error,
		},
	];
}
